(() => {
  const DESKTOP_BREAKPOINT = 1100;
  const BRAND_PAGE_PATH = '/pages/tum-markalar';
  const SELECTORS = {
    desktopTrigger: `.header__inline-menu a[href*="${BRAND_PAGE_PATH}"]`,
    mobileTrigger: `.menu-drawer a[href*="${BRAND_PAGE_PATH}"]`,
  };

  let closeTimer;
  let desktopAbortController;

  const normalizeText = (value = '') => value
    .toLocaleUpperCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/İ/g, 'I')
    .replace(/[^A-Z0-9]/g, '');

  const firstLetter = (value = '') => {
    const normalized = normalizeText(value);
    return /^[A-Z]/.test(normalized) ? normalized.charAt(0) : '#';
  };

  const getUniqueBrandLinks = (root, selector) => {
    const seen = new Set();
    return Array.from(root.querySelectorAll(selector))
      .map((anchor) => ({
        title: anchor.textContent.trim().replace(/\s+/g, ' '),
        href: anchor.getAttribute('href') || '#',
      }))
      .filter((item) => {
        const key = `${item.title}|${item.href}`;
        if (!item.title || item.href.includes(BRAND_PAGE_PATH) || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.title.localeCompare(b.title, 'tr', { sensitivity: 'base' }));
  };

  const createLink = ({ title, href }, className) => {
    const anchor = document.createElement('a');
    anchor.className = className;
    anchor.href = href;
    anchor.textContent = title;
    return anchor;
  };

  const closeDesktopMenu = ({ returnFocus = false } = {}) => {
    window.clearTimeout(closeTimer);
    const panel = document.querySelector('[data-shopping-brand-mega]');
    const trigger = document.querySelector('[data-shopping-brand-trigger]');
    const item = trigger?.closest('.shopping-brand-menu-item');
    const backdrop = document.querySelector('[data-shopping-brand-backdrop]');

    panel?.classList.remove('is-open');
    item?.classList.remove('is-open');
    backdrop?.classList.remove('is-open');
    document.body.classList.remove('shopping-brand-menu-open');
    trigger?.setAttribute('aria-expanded', 'false');

    if (returnFocus) trigger?.focus();
  };

  const openDesktopMenu = () => {
    if (window.innerWidth < DESKTOP_BREAKPOINT) return;
    window.clearTimeout(closeTimer);

    const panel = document.querySelector('[data-shopping-brand-mega]');
    const trigger = document.querySelector('[data-shopping-brand-trigger]');
    const item = trigger?.closest('.shopping-brand-menu-item');
    const backdrop = document.querySelector('[data-shopping-brand-backdrop]');
    const header = document.querySelector('.shopify-section-header');

    if (!panel || !trigger || !item || !backdrop || !header) return;

    backdrop.style.setProperty('--shopping-brand-backdrop-top', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
    panel.classList.add('is-open');
    item.classList.add('is-open');
    backdrop.classList.add('is-open');
    document.body.classList.add('shopping-brand-menu-open');
    trigger.setAttribute('aria-expanded', 'true');
  };

  const scheduleDesktopClose = () => {
    window.clearTimeout(closeTimer);
    closeTimer = window.setTimeout(() => closeDesktopMenu(), 160);
  };

  const buildDesktopMenu = () => {
    desktopAbortController?.abort();
    desktopAbortController = new AbortController();
    const { signal } = desktopAbortController;

    document.querySelectorAll('[data-shopping-brand-mega], [data-shopping-brand-backdrop]').forEach((element) => element.remove());
    document.querySelectorAll('[data-shopping-brand-trigger]').forEach((element) => {
      element.removeAttribute('data-shopping-brand-trigger');
      element.removeAttribute('aria-haspopup');
      element.removeAttribute('aria-expanded');
      element.removeAttribute('aria-controls');
      element.closest('.shopping-brand-menu-item')?.classList.remove('shopping-brand-menu-item', 'is-open');
    });

    const trigger = Array.from(document.querySelectorAll(SELECTORS.desktopTrigger))
      .find((anchor) => anchor.closest('.header__inline-menu'));
    if (!trigger) return;

    const item = trigger.closest('.list-menu-item');
    const headerWrapper = trigger.closest('.header-wrapper');
    const sourceSubmenu = item?.querySelector(':scope > .header__submenu');
    if (!item || !headerWrapper || !sourceSubmenu) return;

    const brands = getUniqueBrandLinks(sourceSubmenu, 'a[href]');
    if (!brands.length) return;

    item.classList.add('shopping-brand-menu-item');
    trigger.dataset.shoppingBrandTrigger = '';
    trigger.setAttribute('aria-haspopup', 'true');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', 'ShoppingBrandMegaMenu');

    const panel = document.createElement('section');
    panel.id = 'ShoppingBrandMegaMenu';
    panel.className = 'shopping-brand-mega';
    panel.dataset.shoppingBrandMega = '';
    panel.setAttribute('aria-label', 'Tüm Markalar');

    const inner = document.createElement('div');
    inner.className = 'shopping-brand-mega__inner container';

    const headingRow = document.createElement('div');
    headingRow.className = 'shopping-brand-mega__heading-row';

    const textWrap = document.createElement('div');
    textWrap.className = 'shopping-brand-mega__text-wrap';

    const title = document.createElement('h2');
    title.className = 'shopping-brand-mega__title';
    title.textContent = 'Tüm Markalar';

    const description = document.createElement('p');
    description.className = 'shopping-brand-mega__description';
    description.textContent = 'Mağazamızda bulunan tüm markaları alfabetik olarak inceleyin. Bir markaya tıklayarak ilgili ürünleri görüntüleyebilirsiniz.';

    const allBrandsLink = createLink(
      { title: 'Tüm Markaları Gör', href: trigger.getAttribute('href') || BRAND_PAGE_PATH },
      'shopping-brand-mega__all-link'
    );
    allBrandsLink.setAttribute('aria-label', 'Tüm markalar sayfasını aç');

    textWrap.append(title, description);
    headingRow.append(textWrap, allBrandsLink);

    const grid = document.createElement('div');
    grid.className = 'shopping-brand-mega__grid';
    brands.forEach((brand) => grid.append(createLink(brand, 'shopping-brand-mega__link')));

    inner.append(headingRow, grid);
    panel.append(inner);
    headerWrapper.append(panel);

    const backdrop = document.createElement('button');
    backdrop.type = 'button';
    backdrop.className = 'shopping-brand-backdrop';
    backdrop.dataset.shoppingBrandBackdrop = '';
    backdrop.tabIndex = -1;
    backdrop.setAttribute('aria-label', 'Markalar menüsünü kapat');
    document.body.append(backdrop);

    item.addEventListener('mouseenter', openDesktopMenu, { signal });
    item.addEventListener('mouseleave', scheduleDesktopClose, { signal });
    item.addEventListener('focusin', openDesktopMenu, { signal });
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget) && !panel.contains(event.relatedTarget)) scheduleDesktopClose();
    }, { signal });
    panel.addEventListener('mouseenter', openDesktopMenu, { signal });
    panel.addEventListener('mouseleave', scheduleDesktopClose, { signal });
    panel.addEventListener('focusin', openDesktopMenu, { signal });
    panel.addEventListener('focusout', (event) => {
      if (!panel.contains(event.relatedTarget) && !item.contains(event.relatedTarget)) scheduleDesktopClose();
    }, { signal });
    backdrop.addEventListener('click', () => closeDesktopMenu(), { signal });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && panel.classList.contains('is-open')) {
        event.preventDefault();
        closeDesktopMenu({ returnFocus: true });
      }
    }, { signal });

    window.addEventListener('resize', () => {
      if (window.innerWidth < DESKTOP_BREAKPOINT) closeDesktopMenu();
      else if (panel.classList.contains('is-open')) openDesktopMenu();
    }, { signal, passive: true });
    window.addEventListener('scroll', () => {
      if (panel.classList.contains('is-open')) openDesktopMenu();
    }, { signal, passive: true });
  };

  const buildMobileBrands = () => {
    document.querySelectorAll('[data-shopping-mobile-brands]').forEach((element) => element.remove());
    document.querySelectorAll('.shopping-mobile-brands').forEach((element) => element.classList.remove('shopping-mobile-brands'));
    document.querySelectorAll('.shopping-mobile-brands__source').forEach((element) => element.classList.remove('shopping-mobile-brands__source'));

    const triggerLink = Array.from(document.querySelectorAll(SELECTORS.mobileTrigger))
      .find((anchor) => anchor.closest('.menu-drawer'));
    const details = triggerLink?.closest('details');
    const submenu = details?.querySelector(':scope > .menu-drawer__submenu');
    const inner = submenu?.querySelector('.menu-drawer__inner-submenu');
    if (!triggerLink || !details || !submenu || !inner) return;

    const sourceList = Array.from(inner.children).find((child) => child.matches('ul.menu-drawer__menu'));
    if (!sourceList) return;

    const brands = getUniqueBrandLinks(sourceList, 'a[href]');
    if (!brands.length) return;

    details.classList.add('shopping-mobile-brands');
    sourceList.classList.add('shopping-mobile-brands__source');

    const shell = document.createElement('div');
    shell.className = 'shopping-mobile-brands__shell';
    shell.dataset.shoppingMobileBrands = '';

    const head = document.createElement('div');
    head.className = 'shopping-mobile-brands__head';

    const titleWrap = document.createElement('div');
    const eyebrow = document.createElement('span');
    eyebrow.className = 'shopping-mobile-brands__eyebrow';
    eyebrow.textContent = 'PARFÜM KUTUSU';
    const title = document.createElement('h2');
    title.className = 'shopping-mobile-brands__title';
    title.textContent = 'Markalar';
    titleWrap.append(eyebrow, title);

    const allLink = createLink(
      { title: 'Tümünü Gör', href: triggerLink.getAttribute('href') || BRAND_PAGE_PATH },
      'shopping-mobile-brands__all-link'
    );
    head.append(titleWrap, allLink);

    const searchWrap = document.createElement('label');
    searchWrap.className = 'shopping-mobile-brands__search';
    searchWrap.innerHTML = '<span class="visually-hidden">Marka ara</span><svg aria-hidden="true" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="6.5" stroke="currentColor" stroke-width="1.5"/><path d="m16 16 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
    const input = document.createElement('input');
    input.type = 'search';
    input.placeholder = 'Marka ara';
    input.autocomplete = 'off';
    input.setAttribute('aria-label', 'Marka ara');
    searchWrap.append(input);

    const groups = new Map();
    brands.forEach((brand) => {
      const letter = firstLetter(brand.title);
      if (!groups.has(letter)) groups.set(letter, []);
      groups.get(letter).push(brand);
    });

    const alphabet = document.createElement('div');
    alphabet.className = 'shopping-mobile-brands__alphabet';
    alphabet.setAttribute('aria-label', 'Marka harfleri');

    const list = document.createElement('div');
    list.className = 'shopping-mobile-brands__groups';

    [...groups.entries()].forEach(([letter, entries]) => {
      const letterButton = document.createElement('button');
      letterButton.type = 'button';
      letterButton.className = 'shopping-mobile-brands__letter-button';
      letterButton.textContent = letter;
      letterButton.setAttribute('aria-label', `${letter} harfine git`);
      alphabet.append(letterButton);

      const group = document.createElement('section');
      group.className = 'shopping-mobile-brands__group';
      group.dataset.brandLetter = letter;

      const heading = document.createElement('h3');
      heading.className = 'shopping-mobile-brands__group-title';
      heading.textContent = letter;

      const links = document.createElement('div');
      links.className = 'shopping-mobile-brands__links';
      entries.forEach((brand) => {
        const anchor = createLink(brand, 'shopping-mobile-brands__link');
        anchor.dataset.brandSearch = normalizeText(brand.title);
        links.append(anchor);
      });

      group.append(heading, links);
      list.append(group);

      letterButton.addEventListener('click', () => {
        const top = inner.scrollTop + group.getBoundingClientRect().top - inner.getBoundingClientRect().top - 12;
        inner.scrollTo({ top, behavior: 'smooth' });
      });
    });

    const empty = document.createElement('p');
    empty.className = 'shopping-mobile-brands__empty';
    empty.textContent = 'Aradığınız marka bulunamadı.';
    empty.hidden = true;

    input.addEventListener('input', () => {
      const query = normalizeText(input.value);
      let visibleCount = 0;

      list.querySelectorAll('.shopping-mobile-brands__group').forEach((group) => {
        let groupVisible = 0;
        group.querySelectorAll('.shopping-mobile-brands__link').forEach((anchor) => {
          const visible = !query || anchor.dataset.brandSearch.includes(query);
          anchor.hidden = !visible;
          if (visible) groupVisible += 1;
        });
        group.hidden = groupVisible === 0;
        visibleCount += groupVisible;
      });

      alphabet.hidden = Boolean(query);
      empty.hidden = visibleCount > 0;
    });

    shell.append(head, searchWrap, alphabet, list, empty);
    inner.insertBefore(shell, sourceList);
  };

  const init = () => {
    buildDesktopMenu();
    buildMobileBrands();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
})();
